import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  ArrowLeft, Loader2, Plus, Trash2, ArrowUpRight, ArrowDownRight,
  Minus, CheckCircle2, XCircle, Target, DollarSign,
  Building2, RefreshCw, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';
import { Footer } from '../components/LegalDisclaimers';
import AIAvatar from '../components/AIAvatar';
import useFormatters from '../lib/useFormatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function DeltaIcon({ direction }) {
  if (direction === 'improved') return <ArrowUpRight className="w-3.5 h-3.5 text-green-500" />;
  if (direction === 'declined') return <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />;
  return <Minus className="w-3.5 h-3.5 text-slate-400" />;
}

function DeltaCell({ label, current, proposed, delta, direction, format, fmt }) {
  const format1 = (v) => {
    if (format === 'dollar') return fmt.money0(v);
    if (format === 'percent') return fmt.percent(v);
    if (format === 'decimal') return new Intl.NumberFormat(fmt.isFr ? 'fr-CA' : 'en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0);
    return fmt.number(v);
  };
  const deltaColor = direction === 'improved' ? 'text-green-500' : direction === 'declined' ? 'text-red-500' : 'text-slate-400';
  return (
    <tr className="border-b border-slate-50 dark:border-slate-800/50">
      <td className="py-2.5 text-xs text-slate-500 dark:text-slate-400">{label}</td>
      <td className="py-2.5 text-right text-xs font-semibold text-slate-700 dark:text-slate-300">{format1(current)}</td>
      <td className="py-2.5 text-right text-xs font-semibold text-slate-700 dark:text-slate-300">{format1(proposed)}</td>
      <td className="py-2.5 text-right">
        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${deltaColor}`}>
          <DeltaIcon direction={direction} />
          {delta > 0 ? '+' : ''}{format1(delta)}
        </span>
      </td>
    </tr>
  );
}

const EMPTY_DEAL = {
  address: '', purchase_price: 0, annual_income: 0, down_payment_percent: 20,
  interest_rate: 5, num_units: 1, annual_property_tax: 0, annual_insurance: 0,
  monthly_maintenance: 0,
};

export default function PortfolioCompare() {
  const { t, i18n } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [removedIds, setRemovedIds] = useState(new Set());
  const [hypotheticals, setHypotheticals] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dealsLoading, setDealsLoading] = useState(true);

  // Load user deals for the removal selector
  useEffect(() => {
    const load = async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true });
        // API returns array directly, not { deals: [...] }
        setDeals(Array.isArray(resp.data) ? resp.data : (resp.data.deals || []));
      } catch (err) {
        if (err.response?.status === 403) navigate('/pricing');
      } finally {
        setDealsLoading(false);
      }
    };
    load();
  }, [navigate]);

  const toggleRemove = (dealId) => {
    setRemovedIds(prev => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  };

  const addHypothetical = () => setHypotheticals(prev => [...prev, { ...EMPTY_DEAL, _key: Date.now() }]);
  const removeHypothetical = (idx) => setHypotheticals(prev => prev.filter((_, i) => i !== idx));
  const updateHypothetical = (idx, field, value) => {
    setHypotheticals(prev => prev.map((h, i) => i === idx ? { ...h, [field]: value } : h));
  };

  const runComparison = useCallback(async () => {
    if (removedIds.size === 0 && hypotheticals.length === 0) return;
    setLoading(true);
    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/portfolio/compare`, {
        remove_deal_ids: Array.from(removedIds),
        add_hypothetical: hypotheticals.map(({ _key, ...h }) => h),
        language: lang,
      }, { withCredentials: true });
      setResult(resp.data);
    } catch (err) {
      console.error('Comparison failed:', err);
    } finally {
      setLoading(false);
    }
  }, [removedIds, hypotheticals]);

  const hasChanges = removedIds.size > 0 || hypotheticals.length > 0;

  if (dealsLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0F1C] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#45C04A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0F1C]" data-testid="portfolio-compare">
      <div className="flex items-center justify-end gap-3 px-6 py-3">
        <Button
          data-testid="back-to-dashboard-btn"
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="text-slate-500 dark:text-slate-400 hover:text-[#12355B] dark:hover:text-white text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          {t('nav.backToDashboard')}
        </Button>
        <Button data-testid="new-analysis-button" onClick={() => navigate('/analyze')} size="sm"
          className="bg-[#12355B] hover:bg-[#1565C0] text-white dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#0F172A] font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> {t('dashboard.newAnalysis')}
        </Button>
        <LanguageSwitcher />
        <DarkModeToggle />
      </div>

      <div className="relative isolate max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Background Image */}
        <div className="absolute inset-0 -mx-4 sm:-mx-6 overflow-hidden -z-10">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/35251cdc-fe6f-4305-b40a-115bc9637197/images/6e16fd70b2939ca78f5767cfd44afba2c9adfee3f72c527185a2556ae3753ae9.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-50/90 dark:bg-[#0A0F1C]/93 backdrop-blur-[2px]" />
        </div>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/portfolio')} className="p-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 transition-colors">
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-300" />
          </button>
          <div>
            <h1 className="font-primary font-bold text-2xl text-[#12355B] dark:text-white">{t('portfolioCompare.title')}</h1>
            <p className="text-xs text-slate-400">{t('portfolioCompare.subtitle')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Action Builder */}
          <div className="lg:col-span-5 space-y-4">
            {/* Remove deals */}
            <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
              <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white mb-3 flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-400" />
                {t('portfolioCompare.removeDeal')}
              </h3>
              <p className="text-xs text-slate-400 mb-3">{t('portfolioCompare.removeDesc')}</p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {deals.map(d => {
                  const isRemoved = removedIds.has(d.deal_id);
                  return (
                    <button
                      key={d.deal_id}
                      onClick={() => toggleRemove(d.deal_id)}
                      data-testid={`toggle-remove-${d.deal_id}`}
                      className={`w-full flex items-center gap-2 p-2.5 rounded-xl text-left text-xs transition-colors border ${
                        isRemoved
                          ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800/30'
                          : 'bg-slate-50 dark:bg-slate-800/50 border-transparent hover:border-slate-200 dark:hover:border-slate-700'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                        isRemoved ? 'bg-red-500 border-red-500' : 'border-slate-300 dark:border-slate-600'
                      }`}>
                        {isRemoved && <X className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`truncate font-medium ${isRemoved ? 'text-red-600 dark:text-red-400 line-through' : 'text-slate-700 dark:text-slate-300'}`}>
                          {d.property_address}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {fmt.money0(d.monthly_cash_flow)}{t('portfolioCompare.perMonthSuffix')} &middot; {t('portfolioCompare.scoreLabel')} {fmt.number(d.deal_score)}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Add hypothetical deals */}
            <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
              <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4 text-green-500" />
                {t('portfolioCompare.addDeal')}
              </h3>
              <p className="text-xs text-slate-400 mb-3">{t('portfolioCompare.addDesc')}</p>

              {hypotheticals.map((h, idx) => (
                <div key={h._key} className="mb-4 bg-green-50 dark:bg-green-950/10 border border-green-200 dark:border-green-800/30 rounded-xl p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-bold text-green-700 dark:text-green-400">{t('portfolioCompare.hypothetical')} #{idx + 1}</span>
                    <button onClick={() => removeHypothetical(idx)} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20">
                      <X className="w-3 h-3 text-red-400" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <input type="text" placeholder={t('portfolioCompare.address')} value={h.address}
                        onChange={e => updateHypothetical(idx, 'address', e.target.value)}
                        className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-green-200 dark:border-green-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300" />
                    </div>
                    {[
                      { field: 'purchase_price', label: t('portfolioCompare.price'), type: 'number' },
                      { field: 'annual_income', label: t('portfolioCompare.income'), type: 'number' },
                      { field: 'down_payment_percent', label: t('portfolioCompare.dp'), type: 'number' },
                      { field: 'interest_rate', label: t('portfolioCompare.rate'), type: 'number' },
                      { field: 'num_units', label: t('portfolioCompare.units'), type: 'number' },
                      { field: 'monthly_maintenance', label: t('portfolioCompare.maintenance'), type: 'number' },
                    ].map(({ field, label, type }) => (
                      <div key={field}>
                        <label className="text-[10px] text-slate-400">{label}</label>
                        <input type={type} value={h[field] || ''}
                          onChange={e => updateHypothetical(idx, field, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-green-200 dark:border-green-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <Button onClick={addHypothetical} variant="outline" size="sm" className="w-full rounded-xl border-dashed border-green-300 dark:border-green-700 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/20">
                <Plus className="w-3 h-3 mr-1" /> {t('portfolioCompare.addAnother')}
              </Button>
            </div>

            {/* Compare Button */}
            <Button
              data-testid="run-comparison-btn"
              onClick={runComparison}
              disabled={!hasChanges || loading}
              className="w-full rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white py-3 font-semibold disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <AIAvatar size={24} className="mr-2 -ml-1" />
              )}
              {loading ? t('portfolioCompare.comparing') : t('portfolioCompare.runComparison')}
            </Button>
          </div>

          {/* Right: Results */}
          <div className="lg:col-span-7 space-y-4">
            {!result && !loading && (
              <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-12 text-center">
                <AIAvatar size={48} className="opacity-60 mx-auto mb-3" label={t('portfolioCompare.emptyTitle')} />
                <h3 className="font-bold text-lg text-[#12355B] dark:text-white mb-1">{t('portfolioCompare.emptyTitle')}</h3>
                <p className="text-sm text-slate-400">{t('portfolioCompare.emptyDesc')}</p>
              </div>
            )}

            {result && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Current */}
                  <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-4">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{t('portfolioCompare.current')}</h4>
                    <div className="text-center mb-3">
                      <span className="text-3xl font-black text-[#12355B] dark:text-white">{fmt.number(result.current.avg_score)}</span>
                      <span className="text-sm text-slate-400">/100</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between"><span className="text-slate-400">{t('portfolioCompare.deals')}</span><span className="font-semibold text-slate-700 dark:text-slate-300">{result.current.deal_count}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">{t('portfolio.monthlyCF')}</span><span className={`font-semibold ${result.current.total_monthly_cf >= 0 ? 'text-green-500' : 'text-red-500'}`}>{fmt.money0(result.current.total_monthly_cf)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">DSCR</span><span className="font-semibold text-slate-700 dark:text-slate-300">{fmt.number(result.current.avg_dscr)}</span></div>
                    </div>
                  </div>
                  {/* Proposed */}
                  <div className="bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/20 dark:to-indigo-950/20 border border-violet-200 dark:border-violet-800/30 rounded-2xl p-4">
                    <h4 className="text-xs font-bold text-violet-500 uppercase tracking-wider mb-3">{t('portfolioCompare.proposed')}</h4>
                    <div className="text-center mb-3">
                      <span className="text-3xl font-black text-violet-600 dark:text-violet-400">{fmt.number(result.proposed.avg_score)}</span>
                      <span className="text-sm text-slate-400">/100</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between"><span className="text-slate-400">{t('portfolioCompare.deals')}</span><span className="font-semibold text-slate-700 dark:text-slate-300">{result.proposed.deal_count}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">{t('portfolio.monthlyCF')}</span><span className={`font-semibold ${result.proposed.total_monthly_cf >= 0 ? 'text-green-500' : 'text-red-500'}`}>{fmt.money0(result.proposed.total_monthly_cf)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">DSCR</span><span className="font-semibold text-slate-700 dark:text-slate-300">{fmt.number(result.proposed.avg_dscr)}</span></div>
                    </div>
                  </div>
                </div>

                {/* Impact Summary */}
                <div className="flex gap-3">
                  <div className="flex-1 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800/30 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-green-500">{result.improvements}</p>
                    <p className="text-[10px] text-green-600 dark:text-green-400 font-semibold uppercase">{t('portfolioCompare.improvements')}</p>
                  </div>
                  <div className="flex-1 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/30 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-red-500">{result.regressions}</p>
                    <p className="text-[10px] text-red-500 font-semibold uppercase">{t('portfolioCompare.regressions')}</p>
                  </div>
                </div>

                {/* Delta Table */}
                <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5 overflow-x-auto">
                  <h4 className="font-primary font-bold text-sm text-[#12355B] dark:text-white mb-3">{t('portfolioCompare.deltaTable')}</h4>
                  <table className="w-full" data-testid="delta-comparison-table">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <th className="text-left py-2 text-xs text-slate-400 font-semibold">{t('portfolioCompare.metric')}</th>
                        <th className="text-right py-2 text-xs text-slate-400 font-semibold">{t('portfolioCompare.current')}</th>
                        <th className="text-right py-2 text-xs text-violet-500 font-semibold">{t('portfolioCompare.proposed')}</th>
                        <th className="text-right py-2 text-xs text-slate-400 font-semibold">{t('portfolioCompare.change')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: 'deal_count', label: t('portfolioCompare.deals'), format: 'number' },
                        { key: 'total_value', label: t('portfolio.totalValue'), format: 'dollar' },
                        { key: 'total_equity', label: t('portfolio.totalEquity'), format: 'dollar' },
                        { key: 'total_monthly_cf', label: t('portfolio.monthlyCF'), format: 'dollar' },
                        { key: 'total_annual_cf', label: t('portfolio.annualCF'), format: 'dollar' },
                        { key: 'avg_cap_rate', label: t('portfolio.avgCapRate'), format: 'percent' },
                        { key: 'avg_dscr', label: t('portfolio.avgDSCR'), format: 'decimal' },
                        { key: 'avg_roi', label: t('portfolio.avgROI'), format: 'percent' },
                        { key: 'avg_score', label: t('portfolio.avgScore'), format: 'number' },
                        { key: 'at_risk', label: t('portfolio.atRisk'), format: 'number' },
                        { key: 'healthy', label: t('portfolio.healthy'), format: 'number' },
                      ].map(({ key, label, format }) => {
                        const d = result.deltas[key] || {};
                        return <DeltaCell key={key} label={label} current={d.current || 0} proposed={d.proposed || 0} delta={d.delta || 0} direction={d.direction || 'unchanged'} format={format} fmt={fmt} />;
                      })}
                    </tbody>
                  </table>
                </div>

                {/* AI Insight */}
                {result.ai_insight?.verdict && (
                  <div className="bg-gradient-to-br from-[#1a1040] to-[#2d1b69] rounded-2xl p-5 text-white" data-testid="ai-comparison-insight">
                    <div className="flex items-center gap-2 mb-3">
                      <AIAvatar size={24} label={t('portfolioCompare.aiInsight')} />
                      <h4 className="font-primary font-bold text-sm">{t('portfolioCompare.aiInsight')}</h4>
                    </div>
                    <p className="text-sm text-white/80 mb-3">{result.ai_insight.verdict}</p>
                    {result.ai_insight.key_impacts?.length > 0 && (
                      <ul className="space-y-1.5 mb-3">
                        {result.ai_insight.key_impacts.map((imp, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-white/70">
                            <Target className="w-3 h-3 mt-0.5 flex-shrink-0 text-violet-400" />{imp}
                          </li>
                        ))}
                      </ul>
                    )}
                    {result.ai_insight.recommendation && (
                      <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-xs text-white/80">{result.ai_insight.recommendation}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
